import React, { useState } from 'react';
import './NearbyRecommend.css';
import skiData from '../services/ski-data';
import fullStarIcon from '../assets/icons/full-star.png';
import halfStarIcon from '../assets/icons/half-star.png';
import emptyStarIcon from '../assets/icons/empty-star.png';

const NearbyRecommend = ({ 
  resorts, 
  selectedResort, 
  onResortSelect,
  onLocationUpdate,
  searchParams,
  onMarkersUpdate,
  onGetLocation
}) => {
  const [radius, setRadius] = useState(searchParams.radius);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isLocating, setIsLocating] = useState(false);

  // 定义固定的范围选项
  const radiusOptions = [10, 20, 50, 100, 200, 500, 1000, 2000, 5000];

  // 生成未来10天的日期选项
  const generateDateOptions = () => {
    const dates = [];
    const today = new Date();
    
    for(let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const formatDate = (date) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return '今天';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return '明天';
    }
    return `${date.getMonth() + 1}月${date.getDate()}日`;
  };

  const handleRadiusChange = (event) => {
    const newRadius = Number(event.target.value);
    setRadius(newRadius);
    onMarkersUpdate(null, { 
      ...searchParams, 
      radius: newRadius 
    }, true);
  };

  const handleDateChange = (event) => {
    const newDate = new Date(event.target.value);
    setSelectedDate(newDate);
    onMarkersUpdate(null, { 
      ...searchParams,
      date: newDate.toISOString().split('T')[0]
    }, true);
  };

  // 添加处理滑雪场点击的函数
  const handleResortClick = async (resort) => {
    if (!resort?.id) {
      console.error('No resort ID provided');
      return;
    }

    try {
      // 加载详细信息
      const detail = await skiData.getResortDetail(resort.id);
      onResortSelect(detail, true);
    } catch (error) {
      console.error('Failed to fetch resort detail:', error);
      // 如果获取详情失败，至少显示基本信息
      onResortSelect(resort, true);
    }
  };

  const dateOptions = generateDateOptions();

  // 修改重新定位的处理函数
  const handleGetLocation = () => {
    setIsLocating(true);
    onGetLocation();
    // 重置定位状态
    setTimeout(() => {
      setIsLocating(false);
    }, 1000);
  };

  // 修改生成星星评分的函数
  const generateSkiStars = (weatherScore) => {
    // 添加安全检查，如果weatherScore无效则使用默认值0
    if (weatherScore === undefined || weatherScore === null || isNaN(weatherScore)) {
      weatherScore = 0;
    }
    
    const score = Math.round(weatherScore * 10);
    const totalStars = 5;
    const fullStars = Math.floor(score / 2);
    const hasHalfStar = score % 2 === 1;
    const emptyStars = totalStars - fullStars - (hasHalfStar ? 1 : 0);
    
    return (
      <>
        {[...Array(Math.max(0, fullStars))].map((_, i) => (
          <img key={`full-${i}`} src={fullStarIcon} alt="★" className="star-icon" />
        ))}
        {hasHalfStar && <img src={halfStarIcon} alt="½" className="star-icon" />}
        {[...Array(Math.max(0, emptyStars))].map((_, i) => (
          <img key={`empty-${i}`} src={emptyStarIcon} alt="☆" className="star-icon" />
        ))}
      </>
    );
  };

  // 修改温度范围格式化函数
  const formatTempRange = (minTemp, maxTemp) => {
    return `温度：${Math.round(minTemp)}~${Math.round(maxTemp)}℃`;
  };

  return (
    <div className="nearby-recommend">
      <div className="nearby-recommend-header">
        <h2>附近滑雪场</h2>
      </div>

      <div className="controls">
        <div className="date-radius-row">
          <div className="date-control">
            <label>选择日期</label>
            <select 
              value={selectedDate.toISOString().split('T')[0]} 
              onChange={handleDateChange}
              className="date-select"
            >
              {dateOptions.map(date => (
                <option 
                  key={date.toISOString()} 
                  value={date.toISOString().split('T')[0]}
                >
                  {formatDate(date)}
                </option>
              ))}
            </select>
          </div>

          <div className="radius-control">
            <label>搜索范围: </label>
            <select 
              value={radius}
              onChange={handleRadiusChange}
              className="radius-select"
            >
              {radiusOptions.map(value => (
                <option key={value} value={value}>
                  {value}km
                </option>
              ))}
            </select>
          </div>
        </div>

        <button 
          className={`location-btn ${isLocating ? 'locating' : ''}`} 
          onClick={handleGetLocation}
          disabled={isLocating}
        >
          {isLocating ? '定位中...' : '重新定位'}
        </button>
      </div>

      <div className="resort-list">
        {resorts.map(resort => (
          <div
            key={resort.id}
            className={`resort-item ${selectedResort?.id === resort.id ? 'selected' : ''}`}
            onClick={() => handleResortClick(resort)}
          >
            <div className="resort-basic-info">
              <div className="resort-name-score">
                <h3>{resort.name}</h3>
                <div className="resort-score">
                  <span>{Number(resort.score).toFixed(1)}</span>
                </div>
              </div>
              <div className="resort-location">
                <span>{resort.region}</span>
                <span>{resort.distance.toFixed(0)}km</span>
              </div>
              <div className="ski-score">              
                <span>适滑指数：{generateSkiStars(resort.weatherScore)}</span>  
              </div>
            </div>

            {/* 天气信息显示 */}
            {resort.weatherForecast && resort.weatherForecast[0] && (
              <div className="resort-weather">
                <span className="weather-desc">
                  {resort.weatherForecast[0].condition}
                </span>
                <span className="weather-temp">
                  {formatTempRange(
                    resort.weatherForecast[0].minTemp,
                    resort.weatherForecast[0].maxTemp
                  )}
                </span>
                <span className="wind-speed">
                  风速：{Math.round(resort.weatherForecast[0].maxWindSpeed)}km/h
                </span>
                {resort.weatherForecast[0].snowfall > 0 && (
                  <span className="snow-depth">
                    降雪：{resort.weatherForecast[0].snowfall.toFixed(1)}cm
                  </span>
                )}
              </div>
            )}

            <div className="resort-stats">
              雪道总长 {resort.totalTrails}km · 
              垂直落差 {resort.verticalDrop}m · 
              缆车数量 {resort.lifts}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NearbyRecommend; 